import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { tradeLicenseApi } from '../../../services/tradeLicenseApi';
import ListPagination from '../../../components/Office/ListPagination';
import './ReportsList.css';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'issued', label: 'Issued' },
  { value: 'rejected', label: 'Rejected' },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: '', label: 'All Methods' },
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'transfer', label: 'Bank Transfer' },
];

const ROLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'verifier', label: 'Verifier' },
  { value: 'inspector', label: 'Inspector' },
  { value: 'approver', label: 'Approver' },
];

const exportTableCsv = (headers, rows, filename) => {
  const csv = [
    headers.join(','),
    ...rows.map((row) =>
      headers.map((header) => `"${String(row[header] ?? '').replace(/"/g, '""')}"`).join(',')
    ),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
};

const ReportsList = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [appSummary, setAppSummary] = useState(null);
  const [revenueSummary, setRevenueSummary] = useState(null);
  const [tableRows, setTableRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });

  const [appFilters, setAppFilters] = useState({ status: '', trade_category: '' });
  const [paymentFilters, setPaymentFilters] = useState({ status: '', payment_method: '' });
  const [stageFilters, setStageFilters] = useState({ stage_id: '', assigned_role: '' });

  const reportDates = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    if (dateRange === 'today') start.setHours(0, 0, 0, 0);
    if (dateRange === 'week') start.setDate(now.getDate() - 7);
    if (dateRange === 'month') start.setMonth(now.getMonth() - 1);
    if (dateRange === 'quarter') start.setMonth(now.getMonth() - 3);
    if (dateRange === 'year') start.setFullYear(now.getFullYear() - 1);
    return {
      start_date: start.toISOString().slice(0, 10),
      end_date: now.toISOString().slice(0, 10),
    };
  }, [dateRange]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [catRes, wfRes] = await Promise.all([
          tradeLicenseApi.getCategories(),
          tradeLicenseApi.getWorkflowStages(),
        ]);
        setCategories(catRes.data.results || catRes.data || []);
        setWorkflows(wfRes.data.results || wfRes.data || []);
      } catch (error) {
        console.error('Failed to load report filters:', error);
      }
    };
    loadMeta();
  }, []);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [appRes, revRes] = await Promise.all([
        tradeLicenseApi.getApplicationSummaryReport(reportDates),
        tradeLicenseApi.getRevenueSummaryReport(reportDates),
      ]);
      setAppSummary(appRes.data);
      setRevenueSummary(revRes.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  }, [reportDates]);

  const fetchTableReport = useCallback(async () => {
    setLoading(true);
    try {
      const baseParams = { ...reportDates, page, page_size: PAGE_SIZE };
      let response;

      if (activeTab === 'applications') {
        response = await tradeLicenseApi.getApplicationRegisterReport({
          ...baseParams,
          ...(appFilters.status ? { status: appFilters.status } : {}),
          ...(appFilters.trade_category ? { trade_category: appFilters.trade_category } : {}),
        });
      } else if (activeTab === 'payments') {
        response = await tradeLicenseApi.getPaymentRegisterReport({
          ...baseParams,
          ...(paymentFilters.status ? { status: paymentFilters.status } : {}),
          ...(paymentFilters.payment_method ? { payment_method: paymentFilters.payment_method } : {}),
        });
      } else if (activeTab === 'pending') {
        response = await tradeLicenseApi.getPendingByStageReport({
          page,
          page_size: PAGE_SIZE,
          ...(stageFilters.stage_id ? { stage_id: stageFilters.stage_id } : {}),
          ...(stageFilters.assigned_role ? { assigned_role: stageFilters.assigned_role } : {}),
        });
      } else {
        return;
      }

      const data = response.data;
      setTableRows(data.results || data || []);
      setPagination({
        count: data.count ?? (Array.isArray(data) ? data.length : 0),
        next: data.next ?? null,
        previous: data.previous ?? null,
      });
    } catch (error) {
      console.error('Failed to fetch table report:', error);
      setTableRows([]);
      setPagination({ count: 0, next: null, previous: null });
    } finally {
      setLoading(false);
    }
  }, [activeTab, reportDates, page, appFilters, paymentFilters, stageFilters]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, dateRange, appFilters, paymentFilters, stageFilters]);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchOverview();
    } else {
      fetchTableReport();
    }
  }, [activeTab, fetchOverview, fetchTableReport, page]);

  const handleExportMetrics = (data, filename) => {
    const csv = `Metric,Value\n${Object.entries(data).map(([key, value]) => `${key},${value}`).join('\n')}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
  };

  const handleExportTable = () => {
    if (activeTab === 'applications') {
      exportTableCsv(
        ['application_number', 'business_name_en', 'applicant_name', 'trade_category', 'status', 'current_stage', 'submitted_date', 'application_fee', 'paid_amount'],
        tableRows.map((row) => ({
          ...row,
          submitted_date: row.submitted_date ? new Date(row.submitted_date).toLocaleDateString() : '',
        })),
        'application_register'
      );
    } else if (activeTab === 'payments') {
      exportTableCsv(
        ['payment_id', 'application_number', 'amount', 'payment_method', 'status', 'paid_date', 'received_by', 'verified_by'],
        tableRows.map((row) => ({
          ...row,
          paid_date: row.paid_date ? new Date(row.paid_date).toLocaleDateString() : '',
        })),
        'payment_register'
      );
    } else if (activeTab === 'pending') {
      exportTableCsv(
        ['application_number', 'business_name_en', 'status', 'current_stage', 'assigned_role', 'assigned_to', 'submitted_date', 'days_pending'],
        tableRows.map((row) => ({
          ...row,
          submitted_date: row.submitted_date ? new Date(row.submitted_date).toLocaleDateString() : '',
        })),
        'pending_by_stage'
      );
    }
  };

  return (
    <div className="reports-container">
      <h1 className="page-title">Reports & Analytics</h1>

      <div className="report-tabs">
        {[
          { id: 'overview', label: 'Overview Metrics' },
          { id: 'applications', label: 'Application Register' },
          { id: 'payments', label: 'Payment Register' },
          { id: 'pending', label: 'Pending by Stage' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`btn ${activeTab === tab.id ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setActiveTab(tab.id); setPage(1); }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab !== 'pending' && (
        <div className="date-filter">
          <label>Period:</label>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
          <span>{reportDates.start_date} to {reportDates.end_date}</span>
        </div>
      )}

      {activeTab === 'applications' && (
        <div className="report-filters">
          <select
            value={appFilters.status}
            onChange={(e) => setAppFilters((prev) => ({ ...prev, status: e.target.value }))}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={appFilters.trade_category}
            onChange={(e) => setAppFilters((prev) => ({ ...prev, trade_category: e.target.value }))}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name_en}</option>
            ))}
          </select>
        </div>
      )}

      {activeTab === 'payments' && (
        <div className="report-filters">
          <select
            value={paymentFilters.status}
            onChange={(e) => setPaymentFilters((prev) => ({ ...prev, status: e.target.value }))}
          >
            {PAYMENT_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={paymentFilters.payment_method}
            onChange={(e) => setPaymentFilters((prev) => ({ ...prev, payment_method: e.target.value }))}
          >
            {PAYMENT_METHOD_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      {activeTab === 'pending' && (
        <div className="report-filters">
          <select
            value={stageFilters.stage_id}
            onChange={(e) => setStageFilters((prev) => ({ ...prev, stage_id: e.target.value }))}
          >
            <option value="">All Stages</option>
            {workflows.filter((wf) => wf.is_active).map((wf) => (
              <option key={wf.id} value={wf.id}>{wf.name_en}</option>
            ))}
          </select>
          <select
            value={stageFilters.assigned_role}
            onChange={(e) => setStageFilters((prev) => ({ ...prev, assigned_role: e.target.value }))}
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading reports...</div>
      ) : activeTab === 'overview' ? (
        <div className="reports-grid">
          {appSummary && (
            <div className="report-card">
              <h2>Application Summary</h2>
              <div className="metrics-grid">
                <div className="metric"><div className="metric-value">{appSummary.total || 0}</div><div className="metric-label">Total</div></div>
                <div className="metric"><div className="metric-value">{appSummary.submitted || 0}</div><div className="metric-label">Submitted</div></div>
                <div className="metric"><div className="metric-value">{appSummary.in_progress || 0}</div><div className="metric-label">In Progress</div></div>
                <div className="metric"><div className="metric-value">{appSummary.pending_approval || 0}</div><div className="metric-label">Pending Approvals</div></div>
                <div className="metric"><div className="metric-value">{appSummary.approved || 0}</div><div className="metric-label">Approved</div></div>
                <div className="metric"><div className="metric-value">{appSummary.paid || 0}</div><div className="metric-label">Paid</div></div>
                <div className="metric"><div className="metric-value">{appSummary.rejected || 0}</div><div className="metric-label">Rejected</div></div>
                <div className="metric"><div className="metric-value">{appSummary.issued || 0}</div><div className="metric-label">Issued</div></div>
              </div>
              <button className="btn btn-secondary" onClick={() => handleExportMetrics(appSummary, 'application_summary')}>
                Export CSV
              </button>
            </div>
          )}
          {revenueSummary && (
            <div className="report-card">
              <h2>Revenue Summary</h2>
              <div className="revenue-details">
                <div className="revenue-item"><span>Total Revenue:</span><strong>₹{Number(revenueSummary.total_revenue || 0).toFixed(2)}</strong></div>
                <div className="revenue-item"><span>Cash:</span><strong>₹{Number(revenueSummary.cash_collections || 0).toFixed(2)}</strong></div>
                <div className="revenue-item"><span>Cheque:</span><strong>₹{Number(revenueSummary.cheque_collections || 0).toFixed(2)}</strong></div>
                <div className="revenue-item"><span>Transfer:</span><strong>₹{Number(revenueSummary.transfer_collections || 0).toFixed(2)}</strong></div>
                <div className="revenue-item"><span>Pending Validation:</span><strong>₹{Number(revenueSummary.pending_validation_amount || 0).toFixed(2)}</strong></div>
              </div>
              <button className="btn btn-secondary" onClick={() => handleExportMetrics(revenueSummary, 'revenue_summary')}>
                Export CSV
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="report-table-card">
            <div className="report-table-toolbar">
              <span>{pagination.count} record(s)</span>
              <button className="btn btn-secondary" onClick={handleExportTable} disabled={tableRows.length === 0}>
                Export CSV
              </button>
            </div>

            {activeTab === 'applications' && (
              <table className="report-data-table">
                <thead>
                  <tr>
                    <th>Application #</th>
                    <th>Business</th>
                    <th>Applicant</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Stage</th>
                    <th>Submitted</th>
                    <th>Fee</th>
                    <th>Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, idx) => (
                    <tr key={`${row.application_number}-${idx}`}>
                      <td>{row.application_number}</td>
                      <td>{row.business_name_en}</td>
                      <td>{row.applicant_name}</td>
                      <td>{row.trade_category}</td>
                      <td>{row.status}</td>
                      <td>{row.current_stage}</td>
                      <td>{row.submitted_date ? new Date(row.submitted_date).toLocaleDateString() : '—'}</td>
                      <td>₹{Number(row.application_fee || 0).toFixed(2)}</td>
                      <td>₹{Number(row.paid_amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {activeTab === 'payments' && (
              <table className="report-data-table">
                <thead>
                  <tr>
                    <th>Payment ID</th>
                    <th>Application</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Received By</th>
                    <th>Verified By</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row) => (
                    <tr key={row.payment_id}>
                      <td>{row.payment_id}</td>
                      <td>{row.application_number}</td>
                      <td>₹{Number(row.amount || 0).toFixed(2)}</td>
                      <td>{row.payment_method}</td>
                      <td>{row.status}</td>
                      <td>{row.paid_date ? new Date(row.paid_date).toLocaleDateString() : '—'}</td>
                      <td>{row.received_by}</td>
                      <td>{row.verified_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {activeTab === 'pending' && (
              <table className="report-data-table">
                <thead>
                  <tr>
                    <th>Application #</th>
                    <th>Business</th>
                    <th>Status</th>
                    <th>Stage</th>
                    <th>Role</th>
                    <th>Assigned To</th>
                    <th>Submitted</th>
                    <th>Days Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, idx) => (
                    <tr key={`${row.application_number}-${idx}`}>
                      <td>{row.application_number}</td>
                      <td>{row.business_name_en}</td>
                      <td>{row.status}</td>
                      <td>{row.current_stage}</td>
                      <td>{row.assigned_role || '—'}</td>
                      <td>{row.assigned_to || '—'}</td>
                      <td>{row.submitted_date ? new Date(row.submitted_date).toLocaleDateString() : '—'}</td>
                      <td>{row.days_pending ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {tableRows.length === 0 && (
              <div className="report-empty">No records found for selected filters.</div>
            )}
          </div>

          <ListPagination
            count={pagination.count}
            next={pagination.next}
            previous={pagination.previous}
            currentPage={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            loading={loading}
          />
        </>
      )}
    </div>
  );
};

export default ReportsList;
